import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(40px); }
  to { opacity: 1; transform: translateY(0); }
`;

/* ═══════════════════════════════════════════
   PAGE HERO
   ═══════════════════════════════════════════ */

export const PageHero = styled.section<{ $bgImage?: string }>`
  min-height: 100vh;
  background-image:
    linear-gradient(180deg,
      rgba(10, 22, 40, 0.15) 0%,
      rgba(10, 22, 40, 0.4) 40%,
      rgba(10, 22, 40, 0.92) 85%,
      var(--bg-primary) 100%),
    url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center 30%;
  background-attachment: fixed;
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: 4rem 1.5rem;

  @media (max-width: 768px) {
    background-attachment: scroll;
    min-height: 85vh;
    background-position: center 20%;
  }
`;

export const HeroInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  width: 100%;
  animation: ${fadeInUp} 0.8s ease-out;
`;

export const HeroTitle = styled.h1`
  font-family: var(--font-heading);
  font-size: clamp(2.8rem, 6vw, 4.5rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 1.2rem;
  text-shadow: 0 4px 30px rgba(0, 0, 0, 0.5);

  span {
    background: var(--gradient-accent);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

export const HeroSubtitle = styled.p`
  font-size: clamp(1rem, 1.8vw, 1.2rem);
  color: var(--text-secondary);
  max-width: 680px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.8;
  margin-bottom: 2.5rem;
`;

export const HeroButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

export const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--gradient-gold);
  color: var(--text-inverse);
  padding: 0.9rem 2.2rem;
  border-radius: var(--border-radius);
  font-family: var(--font-heading);
  font-weight: 700;
  font-size: 0.95rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all var(--transition-base);
  box-shadow: var(--shadow-glow-gold);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 30px rgba(244, 170, 0, 0.4);
    color: var(--text-inverse);
  }
`;

export const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: var(--text-primary);
  border: 2px solid rgba(255, 255, 255, 0.25);
  padding: 0.85rem 2.2rem;
  border-radius: var(--border-radius);
  font-family: var(--font-heading);
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all var(--transition-base);

  &:hover {
    border-color: var(--accent-teal);
    color: var(--accent-teal);
    background: rgba(0, 201, 177, 0.06);
  }
`;

/* ═══════════════════════════════════════════
   SHARED
   ═══════════════════════════════════════════ */

export const SectionEyebrow = styled.div`
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: var(--accent-teal);
  margin-bottom: 0.75rem;
`;

export const SectionTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 3px;
    background: var(--gradient-accent);
    border-radius: 2px;
  }

  &[style*="center"] {
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }
`;

export const BodyText = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: var(--text-secondary);
  margin-bottom: 1.5rem;
`;

/* ═══════════════════════════════════════════
   CATEGORY CARDS
   ═══════════════════════════════════════════ */

export const CategorySection = styled.section`
  padding: 6rem 0;
  background: var(--bg-primary);
`;

export const CategoryContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const CategoryGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 2rem;
`;

export const CategoryCard = styled(Link)<{ $bgImage?: string }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 380px;
  border-radius: var(--border-radius-lg);
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all var(--transition-base);
  text-decoration: none;

  &:nth-child(even) {
    direction: rtl;
    > * { direction: ltr; }
  }

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(0, 201, 177, 0.25);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4), var(--shadow-glow-teal);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    min-height: auto;
    direction: ltr !important;
    > * { direction: ltr !important; }
  }
`;

export const CategoryImage = styled.div<{ $bgImage?: string }>`
  background-image: url(${({ $bgImage }) => $bgImage || ''});
  background-size: cover;
  background-position: center;
  min-height: 380px;
  position: relative;

  @media (max-width: 768px) {
    min-height: 240px;
  }
`;

export const CategoryContent = styled.div`
  padding: 3rem 2.5rem;
  background: var(--bg-secondary);
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;
  }
`;

export const CategoryTagline = styled.div`
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  color: var(--accent-teal);
  margin-bottom: 0.5rem;
`;

export const CategoryTitle = styled.h3`
  font-family: var(--font-heading);
  font-size: clamp(1.6rem, 2.5vw, 2rem);
  font-weight: 800;
  color: var(--text-primary);
  margin-bottom: 0.8rem;
`;

export const CategoryDescription = styled.p`
  font-size: 1rem;
  line-height: 1.8;
  color: var(--text-secondary);
  margin-bottom: 1.2rem;
`;

export const CategoryCta = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--font-heading);
  font-size: 0.85rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--accent-gold);
  transition: gap var(--transition-base);

  ${CategoryCard}:hover & {
    gap: 0.7rem;
  }
`;

/* ═══════════════════════════════════════════
   WHERE WE FISH
   ═══════════════════════════════════════════ */

export const LocationsSection = styled.section`
  padding: 6rem 0;
  background: var(--bg-secondary);
`;

export const LocationsContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const LocationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-top: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

export const LocationCard = styled.div`
  background: var(--gradient-card);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  padding: 2rem;
  transition: all var(--transition-base);

  &:hover {
    transform: translateY(-4px);
    border-color: var(--border-hover);
    box-shadow: var(--shadow-glow-teal);
  }
`;

export const LocationName = styled.h3`
  font-family: var(--font-heading);
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.3rem;
`;

export const LocationDistance = styled.div`
  font-family: var(--font-heading);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--accent-teal);
  margin-bottom: 0.8rem;
`;

export const LocationDescription = styled.p`
  font-size: 0.9rem;
  line-height: 1.7;
  color: var(--text-muted);
  margin-bottom: 0.8rem;
`;

export const LocationSpecies = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

export const SpeciesTag = styled.span`
  font-family: var(--font-heading);
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--accent-teal);
  background: rgba(0, 201, 177, 0.08);
  border: 1px solid rgba(0, 201, 177, 0.2);
  padding: 0.25rem 0.65rem;
  border-radius: 100px;
`;

/* ═══════════════════════════════════════════
   SEASON CALENDAR
   ═══════════════════════════════════════════ */

export const CalendarSection = styled.section`
  padding: 6rem 0;
  background: var(--bg-primary);
`;

export const CalendarContainer = styled.div`
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const CalendarWrapper = styled.div`
  margin-top: 2rem;
  overflow-x: auto;
  border-radius: var(--border-radius-lg);
  border: 1px solid var(--border-color);

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: var(--bg-elevated);
    border-radius: 3px;
  }
`;

export const CalendarTable = styled.table`
  width: 100%;
  min-width: 700px;
  border-collapse: collapse;
  background: var(--gradient-card);
`;

export const CalendarHeader = styled.th`
  font-family: var(--font-heading);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--text-muted);
  background: var(--bg-elevated);
  padding: 0.8rem 0.5rem;
  text-align: center;
  border-bottom: 1px solid var(--border-color);
  white-space: nowrap;

  &:first-child {
    text-align: left;
    padding-left: 1.2rem;
    color: var(--text-primary);
    font-size: 0.75rem;
    min-width: 140px;
  }
`;

export const CalendarRow = styled.tr`
  border-bottom: 1px solid var(--border-color);

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(0, 201, 177, 0.03);
  }
`;

export const CalendarSpecies = styled.td`
  font-family: var(--font-heading);
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-primary);
  padding: 0.8rem 1.2rem;
  white-space: nowrap;
`;

export const CalendarCell = styled.td<{ $status: 'peak' | 'available' | 'off' }>`
  padding: 0.6rem 0.5rem;
  text-align: center;
`;

export const SeasonIndicator = styled.div<{ $status: 'peak' | 'available' | 'off' }>`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  margin: 0 auto;
  background: ${({ $status }) =>
    $status === 'peak'
      ? 'var(--accent-teal)'
      : $status === 'available'
        ? 'rgba(0, 201, 177, 0.25)'
        : 'rgba(255, 255, 255, 0.04)'};
  border: 1px solid ${({ $status }) =>
    $status === 'peak'
      ? 'var(--accent-teal)'
      : $status === 'available'
        ? 'rgba(0, 201, 177, 0.35)'
        : 'rgba(255, 255, 255, 0.06)'};
  transition: transform var(--transition-fast);

  ${CalendarRow}:hover & {
    transform: scale(1.15);
  }
`;

export const CalendarLegend = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-top: 1rem;
  padding: 0 0.5rem;

  @media (max-width: 560px) {
    flex-wrap: wrap;
    gap: 1rem;
  }
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: var(--text-muted);
`;

export const LegendDot = styled.div<{ $status: 'peak' | 'available' | 'off' }>`
  width: 14px;
  height: 14px;
  border-radius: 4px;
  background: ${({ $status }) =>
    $status === 'peak'
      ? 'var(--accent-teal)'
      : $status === 'available'
        ? 'rgba(0, 201, 177, 0.25)'
        : 'rgba(255, 255, 255, 0.04)'};
  border: 1px solid ${({ $status }) =>
    $status === 'peak'
      ? 'var(--accent-teal)'
      : $status === 'available'
        ? 'rgba(0, 201, 177, 0.35)'
        : 'rgba(255, 255, 255, 0.06)'};
`;

/* ═══════════════════════════════════════════
   CTA SECTION
   ═══════════════════════════════════════════ */

export const CtaSection = styled.section`
  padding: 5rem 0;
  background: var(--bg-secondary);
  text-align: center;
  position: relative;

  &::before, &::after {
    content: '';
    position: absolute;
    left: 0; right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, var(--accent-gold), transparent);
    opacity: 0.25;
  }
  &::before { top: 0; }
  &::after { bottom: 0; }
`;

export const CtaContainer = styled.div`
  max-width: 700px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

export const CtaTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: clamp(2rem, 4vw, 2.8rem);
  font-weight: 800;
  margin-bottom: 1rem;
  color: var(--text-primary);
`;

export const CtaSubtext = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  margin-bottom: 2rem;
  line-height: 1.7;
`;

export const CtaButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--gradient-gold);
  color: var(--text-inverse);
  padding: 1rem 2.5rem;
  border-radius: var(--border-radius);
  font-family: var(--font-heading);
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 1px;
  text-transform: uppercase;
  transition: all var(--transition-base);
  box-shadow: var(--shadow-glow-gold);

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 30px rgba(244, 170, 0, 0.4);
    color: var(--text-inverse);
  }
`;
